<template>
  <div
    class="kv-ve-text"
    ref="root"
    :class="{ 'kv-ve-text--selected': selected }"
    :style="textBlockStyle"
  >
    <div
      v-if="editor && selected"
      v-show="toolbarState.visible"
      ref="toolbar"
      class="kv-ve-text__toolbar kv-ve-text__toolbar--bubble"
      :style="toolbarStyle"
      @mousedown.stop="onToolbarMouseDown"
    >
        <select class="kv-ve-text__select" :value="activeFontFamily" @change="setFontFamily($event.target.value)" :title="$_('visualEditor-emailEditor-tiptap-font')">
          <option value="">{{ $_('visualEditor-emailEditor-tiptap-bodyFont') }}</option>
          <option value="Helvetica, Arial, sans-serif">{{ $_('visualEditor-emailEditor-tiptap-fontHelvetica') }}</option>
          <option value="Arial, Helvetica, sans-serif">{{ $_('visualEditor-emailEditor-tiptap-fontArial') }}</option>
          <option value="Georgia, serif">{{ $_('visualEditor-emailEditor-tiptap-fontGeorgia') }}</option>
          <option value="'Times New Roman', Times, serif">{{ $_('visualEditor-emailEditor-tiptap-fontTimes') }}</option>
        </select>

        <select class="kv-ve-text__select kv-ve-text__select--size" :value="activeFontSize" @change="setFontSize($event.target.value)" :title="$_('visualEditor-emailEditor-tiptap-size')">
          <option value="">—</option>
          <option v-for="n in fontSizes" :key="n" :value="String(n)">{{ n }}</option>
        </select>

        <select class="kv-ve-text__select kv-ve-text__select--lh" :value="activeLineHeight" @change="setLineHeight($event.target.value)" :title="$_('visualEditor-emailEditor-tiptap-lineHeight')">
          <option value="">{{ $_('visualEditor-emailEditor-tiptap-lineHeightShort') }}</option>
          <option value="0.8">0.8</option>
          <option value="0.9">0.9</option>
          <option value="1.0">1.0</option>
          <option value="1.1">1.1</option>
          <option value="1.2">1.2</option>
          <option value="1.3">1.3</option>
          <option value="1.4">1.4</option>
          <option value="1.5">1.5</option>
          <option value="1.6">1.6</option>
          <option value="1.7">1.7</option>
          <option value="1.8">1.8</option>
          <option value="1.9">1.9</option>
          <option value="2.0">2.0</option>
          <option value="2.2">2.2</option>
          <option value="2.4">2.4</option>
          <option value="2.6">2.6</option>
          <option value="2.8">2.8</option>
          <option value="3.0">3.0</option>
        </select>

        <label
          class="kv-ve-text__color-btn"
          :title="$_('visualEditor-emailEditor-tiptap-textColor')"
          :style="{ '--kv-ve-color': activeColor }"
        >
          <span class="kv-ve-text__icon-a" aria-hidden="true">{{ $_('visualEditor-emailEditor-tiptap-textGlyph') }}</span>
          <input type="color" :value="activeColor" @input="setColor($event.target.value)" />
        </label>

        <div class="kv-ve-text__bg-wrap" :title="$_('visualEditor-emailEditor-tiptap-textBackground')">
          <label
            class="kv-ve-text__color-btn kv-ve-text__color-btn--bg"
            :style="{ '--kv-ve-color': activeColor, '--kv-ve-bg': activeBackgroundColorCss }"
            :title="$_('visualEditor-emailEditor-tiptap-textBackground')"
          >
            <span class="kv-ve-text__icon-bg" aria-hidden="true">
              <span class="kv-ve-text__icon-a kv-ve-text__icon-a--on-bg">{{ $_('visualEditor-emailEditor-tiptap-textGlyph') }}</span>
            </span>
            <input type="color" :value="backgroundColorForInput" @input="setBackgroundColor($event.target.value)" />
          </label>
          <button
            v-if="hasBackgroundColor"
            type="button"
            class="kv-ve-text__bg-clear"
            :title="$_('visualEditor-emailEditor-tiptap-removeBackground')"
            :aria-label="$_('visualEditor-emailEditor-tiptap-removeBackground')"
            @click="unsetBackgroundColor"
          >
            <span class="mdi mdi-close" aria-hidden="true"></span>
          </button>
        </div>

        <span class="kv-ve-text__sep"></span>
        <button type="button" class="kv-ve-text__btn" :class="{ active: editor.isActive('bold') }" @click="toggleBold" :title="$_('visualEditor-emailEditor-tiptap-bold')" :aria-label="$_('visualEditor-emailEditor-tiptap-bold')">
          <span class="mdi mdi-format-bold" aria-hidden="true"></span>
        </button>
        <button type="button" class="kv-ve-text__btn" :class="{ active: editor.isActive('italic') }" @click="toggleItalic" :title="$_('visualEditor-emailEditor-tiptap-italic')" :aria-label="$_('visualEditor-emailEditor-tiptap-italic')">
          <span class="mdi mdi-format-italic" aria-hidden="true"></span>
        </button>
        <button type="button" class="kv-ve-text__btn" :class="{ active: editor.isActive('underline') }" @click="toggleUnderline" :title="$_('visualEditor-emailEditor-tiptap-underline')" :aria-label="$_('visualEditor-emailEditor-tiptap-underline')">
          <span class="mdi mdi-format-underline" aria-hidden="true"></span>
        </button>
        <button type="button" class="kv-ve-text__btn kv-ve-text__btn--strike" :class="{ active: editor.isActive('strike') }" @click="toggleStrike" :title="$_('visualEditor-emailEditor-tiptap-strike')" :aria-label="$_('visualEditor-emailEditor-tiptap-strike')">
          <span class="mdi mdi-format-strikethrough" aria-hidden="true"></span>
        </button>
        <span class="kv-ve-text__sep"></span>
        <div class="kv-ve-text__link-wrap" @keydown.esc.prevent.stop="closeLinkUi">
          <button
            type="button"
            class="kv-ve-text__btn"
            :class="{ active: editor.isActive('link') || linkUiOpen }"
            @click="toggleLinkUi"
            :title="$_('visualEditor-emailEditor-common-link')"
            :aria-label="$_('visualEditor-emailEditor-common-link')"
          >
            <span class="mdi mdi-link-variant" aria-hidden="true"></span>
          </button>
        </div>
      </div>

    <div
      v-if="linkUiOpen"
      class="kv-ve-text__modal-backdrop"
      role="presentation"
      @mousedown.self="closeLinkUi"
      @keydown.esc.prevent.stop="closeLinkUi"
    >
      <div class="kv-ve-text__modal" role="dialog" aria-modal="true" :aria-label="$_('visualEditor-emailEditor-tiptap-link-title')" @mousedown.stop>
        <div class="kv-ve-text__link-header">
          <div class="kv-ve-text__link-title">{{ $_("visualEditor-emailEditor-tiptap-link-title") }}</div>
          <button type="button" class="kv-ve-text__link-close" :title="$_('visualEditor-emailEditor-tiptap-link-close')" @click="closeLinkUi">
            <span class="mdi mdi-close" aria-hidden="true"></span>
          </button>
        </div>

        <div class="kv-ve-text__link-body">
          <label class="kv-ve-text__link-label">{{ $_("visualEditor-emailEditor-tiptap-link-linkTo") }}</label>
          <select class="kv-ve-text__link-select" v-model="linkForm.type">
            <option value="web">{{ $_("visualEditor-emailEditor-tiptap-link-type-web") }}</option>
            <option value="email">{{ $_("visualEditor-emailEditor-tiptap-link-type-email") }}</option>
            <option value="whatsapp">{{ $_("visualEditor-emailEditor-tiptap-link-type-whatsapp") }}</option>
            <option value="phone">{{ $_("visualEditor-emailEditor-tiptap-link-type-phone") }}</option>
            <option value="descadastro">{{ $_("visualEditor-emailEditor-tiptap-link-type-unsubscribe") }}</option>
          </select>

          <template v-if="linkForm.type === 'web'">
            <label class="kv-ve-text__link-label">{{ $_("visualEditor-emailEditor-tiptap-link-web-url") }}</label>
            <input
              ref="linkWebInput"
              class="kv-ve-text__link-input"
              type="url"
              inputmode="url"
              autocomplete="off"
              spellcheck="false"
              :placeholder="$_('visualEditor-emailEditor-tiptap-link-web-placeholder')"
              v-model="linkForm.webUrl"
              @keydown.enter.prevent="applyLinkForm"
            />
            <div v-if="linkErrors.webUrl" class="kv-ve-text__link-error">{{ linkErrors.webUrl }}</div>
          </template>

          <template v-else-if="linkForm.type === 'email'">
            <label class="kv-ve-text__link-label">{{ $_("visualEditor-emailEditor-tiptap-link-email-address") }}</label>
            <input
              ref="linkEmailInput"
              class="kv-ve-text__link-input"
              type="email"
              inputmode="email"
              autocomplete="off"
              spellcheck="false"
              :placeholder="$_('visualEditor-emailEditor-tiptap-link-email-placeholder')"
              v-model="linkForm.emailAddress"
              @keydown.enter.prevent="applyLinkForm"
            />
            <label class="kv-ve-text__link-label">{{ $_("visualEditor-emailEditor-tiptap-link-email-subject") }}</label>
            <input class="kv-ve-text__link-input" type="text" v-model="linkForm.emailSubject" />
            <label class="kv-ve-text__link-label">{{ $_("visualEditor-emailEditor-tiptap-link-email-body") }}</label>
            <textarea class="kv-ve-text__link-textarea" rows="3" v-model="linkForm.emailBody"></textarea>
          </template>

          <template v-else-if="linkForm.type === 'whatsapp'">
            <label class="kv-ve-text__link-label">{{ $_("visualEditor-emailEditor-tiptap-link-whatsapp-phone") }}</label>
            <input
              ref="linkWhatsappPhoneInput"
              class="kv-ve-text__link-input"
              type="tel"
              inputmode="tel"
              autocomplete="off"
              spellcheck="false"
              placeholder="(##) #####-####"
              v-mask="'(##) #####-####'"
              v-model="linkForm.whatsappPhone"
              @keydown.enter.prevent="applyLinkForm"
            />
            <label class="kv-ve-text__link-label">{{ $_("visualEditor-emailEditor-tiptap-link-whatsapp-message") }}</label>
            <textarea class="kv-ve-text__link-textarea" rows="3" v-model="linkForm.whatsappMessage"></textarea>
          </template>

          <template v-else>
            <template v-if="linkForm.type === 'descadastro'">
              <div class="kv-ve-text__link-descadastro-hint">
                {{ $_("visualEditor-emailEditor-tiptap-link-unsubscribeHint") }}
              </div>
            </template>
            <template v-else>
            <label class="kv-ve-text__link-label">{{ $_("visualEditor-emailEditor-tiptap-link-phone-number") }}</label>
            <input
              ref="linkPhoneInput"
              class="kv-ve-text__link-input"
              type="tel"
              inputmode="tel"
              autocomplete="off"
              spellcheck="false"
              placeholder="(##) #####-####"
              v-mask="'(##) #####-####'"
              v-model="linkForm.phoneNumber"
              @keydown.enter.prevent="applyLinkForm"
            />
            </template>
          </template>

          <div class="kv-ve-text__link-actions">
            <button
              type="button"
              class="kv-ve-text__link-remove"
              :disabled="!editor.isActive('link')"
              @click="removeLink"
            >
              {{ $_("visualEditor-emailEditor-tiptap-link-remove") }}
            </button>
            <button type="button" class="kv-ve-text__link-save" :disabled="!isLinkFormValid" @click="applyLinkForm">
              {{ $_("visualEditor-emailEditor-tiptap-link-save") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="snippetsUi.open"
      class="kv-ve-text__snippets"
      :style="snippetsDropdownStyle"
      role="listbox"
      :aria-label="$_('visualEditor-emailEditor-tiptap-snippets')"
      @mousedown.prevent.stop
    >
      <button
        v-for="(s, i) in snippetsUi.items"
        :key="s.token + ':' + i"
        type="button"
        class="kv-ve-text__snippet-item"
        :class="{ active: i === snippetsUi.activeIdx }"
        role="option"
        :aria-selected="i === snippetsUi.activeIdx ? 'true' : 'false'"
        @mouseenter="snippetsUi.activeIdx = i"
        @click="insertSnippet(s)"
      >
        <div class="kv-ve-text__snippet-token">{{ s.token }}</div>
      </button>
    </div>

    <editor-content v-if="editor" class="kv-ve-text__content" :editor="editor" />
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import { getEmailTiptapExtensions } from "../lib/tiptapExtensions";

export default {
  name: "VeTextBlock",
  components: { EditorContent },
  props: {
    block: { type: Object, required: true },
    selected: { type: Boolean, default: false },
  },
  inject: {
    kvVeGetSnippets: { default: null },
  },
  data() {
    return {
      editor: null,
      fontSizes: [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48],
      linkUiOpen: false,
      // Snippets autocomplete (trigger: [[)
      snippetsUi: {
        open: false,
        query: "",
        items: [],
        activeIdx: 0,
        from: null,
        to: null,
        left: 0,
        top: 0,
      },
      toolbarState: {
        visible: false,
        top: 0,
        left: 0,
        placement: "top", // top | bottom
      },
      _toolbarRaf: null,
      linkForm: {
        type: "web", // web | email | whatsapp | phone | descadastro
        // web
        webUrl: "",
        // email
        emailAddress: "",
        emailSubject: "",
        emailBody: "",
        // whatsapp
        whatsappPhone: "",
        whatsappMessage: "",
        // phone
        phoneNumber: "",
      },
      linkErrors: {
        webUrl: "",
      },
    };
  },
  computed: {
    alignCss() {
      const v = String(this.block?.settings?.align || "").toLowerCase();
      return v === "left" || v === "center" || v === "right" ? v : null;
    },
    displayMode() {
      const v = String(this.block?.settings?.display || "").toLowerCase();
      // Back-compat: "inline" -> "inline-block"
      return v === "inline-block" || v === "inline" ? "inline-block" : "block";
    },
    textBlockStyle() {
      const style = {
        textAlign: this.alignCss,
        display: this.displayMode,
      };

      // Width support for inline-block text elements
      if (this.displayMode === "inline-block") {
        const s = this.block?.settings || {};
        const widthModeRaw = String(s.widthMode || "").toLowerCase();
        const widthMode = widthModeRaw === "px" || widthModeRaw === "pct" ? widthModeRaw : "pct";
        const widthPx = Number(s.widthPx);
        const w = Number.isFinite(widthPx) && widthPx > 0 ? `${Math.round(widthPx)}px` : null;
        const widthPctRaw = Number(s.widthPct);
        const widthPct = Number.isFinite(widthPctRaw) ? Math.min(100, Math.max(10, Math.round(widthPctRaw))) : 100;
        const wp = `${widthPct}%`;

        if (widthMode === "px" && w) {
          style.width = w;
          style.maxWidth = w;
        } else if (widthMode === "pct") {
          style.width = wp;
          style.maxWidth = "100%";
        }
      }

      return style;
    },
    activeColor() {
      if (!this.editor) return "#111827";
      return this.editor.getAttributes("textStyle")?.color || "#111827";
    },
    hasBackgroundColor() {
      if (!this.editor) return false;
      const v = this.editor.getAttributes("textStyle")?.backgroundColor;
      return Boolean(v);
    },
    activeBackgroundColorCss() {
      if (!this.editor) return "transparent";
      const v = this.editor.getAttributes("textStyle")?.backgroundColor;
      const s = String(v || "").trim();
      // When unset, keep it transparent (default requested).
      if (!s) return "transparent";
      return s;
    },
    backgroundColorForInput() {
      // Native <input type="color"> can't represent transparent.
      // When unset, we show a neutral default, but do not apply it until user picks.
      const v = this.activeBackgroundColorCss;
      return /^#([0-9a-f]{6})$/i.test(String(v)) ? String(v) : "#ffffff";
    },
    activeFontFamily() {
      if (!this.editor) return "";
      return this.editor.getAttributes("textStyle")?.fontFamily || "";
    },
    activeFontSize() {
      if (!this.editor) return "";
      const fs = this.editor.getAttributes("textStyle")?.fontSize || "";
      const m = String(fs).match(/^(\d+)(px)?$/);
      return m ? m[1] : "";
    },
    activeLineHeight() {
      if (!this.editor) return "";
      return this.editor.getAttributes("textStyle")?.lineHeight || "";
    },
    isWebUrlValid() {
      if (this.linkForm.type !== "web") return true;
      const v = String(this.linkForm.webUrl || "").trim();
      if (!v) return false;
      return /^https?:\/\//i.test(v);
    },
    isLinkFormValid() {
      const t = this.linkForm.type;
      if (t === "web") return this.isWebUrlValid;
      if (t === "email") return Boolean(String(this.linkForm.emailAddress || "").trim());
      if (t === "whatsapp") return Boolean(this.safeWhatsappPhone(this.linkForm.whatsappPhone));
      if (t === "phone") return Boolean(this.safePhone(this.linkForm.phoneNumber));
      if (t === "descadastro") return true;
      return false;
    },
    toolbarStyle() {
      // On mobile, keep the toolbar pinned at the top for better UX (selection toolbars can jump
      // around and cause accidental native selection/scroll issues).
      const isMobile =
        (window.matchMedia && window.matchMedia("(max-width: 768px)").matches) ||
        (window.innerWidth || 0) <= 768;
      if (isMobile) {
        return {
          top: "calc(env(safe-area-inset-top, 0px) + 8px)",
          left: "50%",
          right: "auto",
          bottom: "auto",
          transform: "translateX(-50%)",
        };
      }

      const t = this.toolbarState;
      return {
        top: `${Math.round(t.top)}px`,
        left: `${Math.round(t.left)}px`,
        transform: "translate(-50%, calc(-100% - 10px))",
      };
    },
    availableSnippets() {
      const raw = (typeof this.kvVeGetSnippets === "function" ? this.kvVeGetSnippets() : null) || [];
      const list = Array.isArray(raw) ? raw : [];
      return list
        .map((x) => {
          if (!x) return null;
          if (typeof x === "string") {
            const t = x.trim();
            return t ? { token: this.ensureWrappedToken(t), label: t, description: "" } : null;
          }
          const tokenRaw = x.token ?? x.value ?? x.key ?? x.id ?? "";
          const labelRaw = x.label ?? x.name ?? tokenRaw ?? "";
          const descRaw = x.description ?? x.desc ?? "";
          const token = this.ensureWrappedToken(String(tokenRaw || "").trim());
          const label = String(labelRaw || "").trim() || token;
          const description = String(descRaw || "").trim();
          return token ? { token, label, description } : null;
        })
        .filter(Boolean);
    },
    snippetsDropdownStyle() {
      return {
        left: `${Math.round(this.snippetsUi.left)}px`,
        top: `${Math.round(this.snippetsUi.top)}px`,
      };
    },
  },
  mounted() {
    const incoming = this.block?.settings?.content?.tiptapJson;
    const tiptapJson =
      incoming || {
        type: "doc",
        content: [{ type: "paragraph" }],
      };
    this.editor = new Editor({
      extensions: getEmailTiptapExtensions({ withPlaceholder: true }),
      editorProps: {
        // Kevi has global CSS like `* { font-family: Montserrat; }` applied directly to every element.
        // Inheritance from the editor container won't win against that, so we force our base
        // typography on the ProseMirror root via inline styles (still allowing inline marks to override).
        // Note: line-height is NOT set here so that TipTap's lineHeight extension can control it.
        attributes: {
          style: [
            "font-family: var(--kv-ve-font-family, Helvetica, Arial, sans-serif)",
            "font-size: var(--kv-ve-font-size, 16px)",
            "font-weight: 400",
            "color: var(--kv-ve-text-color, #111827)",
            "text-transform: none",
            "letter-spacing: normal",
          ].join(";"),
        },
        handleKeyDown: (_view, event) => {
          if (this.handleSnippetsKeydown(event)) return true;
          return false;
        },
      },
      content: tiptapJson,
      editable: Boolean(this.selected),
      onUpdate: ({ editor }) => {
        if (!this.block.settings) this.$set(this.block, "settings", {});
        this.$set(this.block.settings, "content", {
          tiptapJson: editor.getJSON(),
        });
      },
    });

    // Bubble toolbar positioning
    this.editor.on?.("selectionUpdate", this.scheduleToolbarUpdate);
    this.editor.on?.("transaction", this.scheduleToolbarUpdate);
    this.editor.on?.("transaction", this.updateSnippetsUiFromCursor);
    this.editor.on?.("selectionUpdate", this.updateSnippetsUiFromCursor);
    this.editor.on?.("focus", this.scheduleToolbarUpdate);
    this.editor.on?.("blur", ({ event } = {}) => {
      // Hide when leaving the editor (unless link modal is open).
      // BUT: allow focusing toolbar controls (select/color input) without collapsing the bubble bar.
      if (this.linkUiOpen) return;
      const toolbarEl = this.$refs.toolbar;
      const relatedTarget = event?.relatedTarget || document.activeElement;
      if (toolbarEl && relatedTarget && toolbarEl.contains(relatedTarget)) return;
      this.toolbarState.visible = false;
    });

    window.addEventListener("scroll", this.scheduleToolbarUpdate, true);
    window.addEventListener("resize", this.scheduleToolbarUpdate);
    this.$nextTick(() => this.scheduleToolbarUpdate());
    this.$nextTick(() => this.updateSnippetsUiFromCursor());
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.scheduleToolbarUpdate, true);
    window.removeEventListener("resize", this.scheduleToolbarUpdate);
    if (this._toolbarRaf) window.cancelAnimationFrame(this._toolbarRaf);
    this.editor?.destroy?.();
  },
  watch: {
    selected(next) {
      this.editor?.setOptions?.({ editable: Boolean(next) });
      if (!next) this.closeLinkUi();
      if (!next) this.closeSnippetsUi();
      if (!next) this.toolbarState.visible = false;
      else this.$nextTick(() => this.scheduleToolbarUpdate());
    },
    linkUiOpen(next) {
      if (next) this.$nextTick(() => this.scheduleToolbarUpdate());
      else this.$nextTick(() => this.scheduleToolbarUpdate());
    },
    "linkForm.type"() {
      // When switching types, focus the primary field for that type.
      if (!this.linkUiOpen) return;
      this.$nextTick(() => this.focusLinkPrimaryField());
    },
    "linkForm.webUrl"() {
      if (this.linkErrors.webUrl) this.linkErrors.webUrl = "";
    },
    "block.settings.content": {
      deep: true,
      handler(incoming) {
        if (!this.editor) return;
        const nextJson = incoming?.tiptapJson;
        if (!nextJson) return;
        const currentJson = this.editor.getJSON();
        const a = JSON.stringify(currentJson);
        const b = JSON.stringify(nextJson);
        if (a !== b) {
          this.editor.commands.setContent(nextJson, false);
        }
      },
    },
  },
  methods: {
    ensureWrappedToken(raw) {
      const t = String(raw || "").trim();
      if (!t) return "";
      if (t.startsWith("[[")) return t;
      // Accept passing "cliente.nome" and wrap.
      return `[[${t.replace(/^\[+|\]+$/g, "")}]]`;
    },
    closeSnippetsUi() {
      this.snippetsUi.open = false;
      this.snippetsUi.query = "";
      this.snippetsUi.items = [];
      this.snippetsUi.activeIdx = 0;
      this.snippetsUi.from = null;
      this.snippetsUi.to = null;
    },
    updateSnippetsUiFromCursor() {
      if (!this.editor || !this.selected) {
        this.closeSnippetsUi();
        return;
      }
      if (this.linkUiOpen) {
        // Avoid overlapping popups.
        this.closeSnippetsUi();
        return;
      }
      const view = this.editor.view;
      const sel = this.editor.state.selection;
      if (!view || !sel) return;
      if (!sel.empty) {
        this.closeSnippetsUi();
        return;
      }

      const $from = sel.$from;
      const parentText = $from.parent?.textBetween?.(0, $from.parentOffset, "\0", "\0") || "";
      const idx = parentText.lastIndexOf("[[");
      if (idx < 0) {
        this.closeSnippetsUi();
        return;
      }
      const query = parentText.slice(idx + 2);
      // Stop suggestions when user closes token or types whitespace/newline.
      if (query.includes("]]") || /\s/.test(query)) {
        this.closeSnippetsUi();
        return;
      }

      const needle = String(query || "").trim().toLowerCase();
      const all = this.availableSnippets;
      const items = (all || [])
        .filter((s) => {
          if (!needle) return true;
          return (
            String(s.token || "").toLowerCase().includes(needle) ||
            String(s.label || "").toLowerCase().includes(needle)
          );
        })
        .slice(0, 30);

      if (!items.length) {
        this.closeSnippetsUi();
        return;
      }

      // Map parent-relative offset to doc positions
      const from = $from.start() + idx;
      const to = sel.from;

      // Anchor dropdown at caret
      let left = 0;
      let top = 0;
      try {
        const caret = view.coordsAtPos(sel.from);
        left = caret.left;
        top = caret.bottom + 8;
      } catch (e) {
        // ignore
      }

      this.snippetsUi.open = true;
      this.snippetsUi.query = query;
      this.snippetsUi.items = items;
      this.snippetsUi.activeIdx = Math.min(this.snippetsUi.activeIdx || 0, items.length - 1);
      this.snippetsUi.from = from;
      this.snippetsUi.to = to;
      this.snippetsUi.left = left;
      this.snippetsUi.top = top;
    },
    handleSnippetsKeydown(event) {
      if (!this.snippetsUi.open) return false;
      const items = this.snippetsUi.items || [];
      if (!items.length) return false;
      const key = String(event?.key || "");
      if (key === "Escape") {
        event.preventDefault();
        this.closeSnippetsUi();
        return true;
      }
      if (key === "ArrowDown") {
        event.preventDefault();
        this.snippetsUi.activeIdx = (this.snippetsUi.activeIdx + 1) % items.length;
        return true;
      }
      if (key === "ArrowUp") {
        event.preventDefault();
        this.snippetsUi.activeIdx = (this.snippetsUi.activeIdx - 1 + items.length) % items.length;
        return true;
      }
      if (key === "Enter" || key === "Tab") {
        event.preventDefault();
        this.insertSnippet(items[this.snippetsUi.activeIdx]);
        return true;
      }
      return false;
    },
    insertSnippet(snippet) {
      if (!this.editor) return;
      const token = this.ensureWrappedToken(snippet?.token || snippet?.label || "");
      if (!token) return;
      const from = this.snippetsUi.from;
      const to = this.snippetsUi.to;
      if (typeof from !== "number" || typeof to !== "number") return;
      this.editor.chain().focus().insertContentAt({ from, to }, token).run();
      this.closeSnippetsUi();
    },
    onToolbarMouseDown(e) {
      // Important: Bubble menus often `preventDefault()` on mousedown to keep the editor selection.
      // But doing that on a wrapper breaks native form controls (selects won't open).
      // So we only prevent default for non-form-control interactions.
      const t = e?.target;
      const isFormControl = t?.closest?.("select, input, textarea, label");
      if (isFormControl) return;
      e?.preventDefault?.();
    },
    scheduleToolbarUpdate() {
      if (!this.editor || !this.selected) return;
      if (this._toolbarRaf) return;
      this._toolbarRaf = window.requestAnimationFrame(() => {
        this._toolbarRaf = null;
        this.updateToolbarPosition();
      });
    },
    updateToolbarPosition() {
      if (!this.editor || !this.selected) {
        this.toolbarState.visible = false;
        return;
      }

      // Show only when the editor is focused (or link modal is open).
      const isFocused = this.editor?.isFocused;
      const focused = typeof isFocused === "function" ? Boolean(isFocused.call(this.editor)) : Boolean(isFocused);
      const toolbarEl = this.$refs.toolbar;
      const toolbarHasFocus = Boolean(toolbarEl && document.activeElement && toolbarEl.contains(document.activeElement));
      if (!focused && !toolbarHasFocus && !this.linkUiOpen) {
        this.toolbarState.visible = false;
        return;
      }

      // Only show the toolbar when there is an actual text selection.
      // (Keep it while link UI is open so the user can finish the action.)
      const sel = this.editor?.state?.selection;
      if (sel?.empty && !toolbarHasFocus && !this.linkUiOpen) {
        this.toolbarState.visible = false;
        return;
      }

      const rootEl = this.$refs.root;
      const view = this.editor?.view;
      if (!rootEl || !toolbarEl || !view) return;

      try {
        const { from, to } = this.editor.state.selection;
        const a = view.coordsAtPos(from);
        const b = view.coordsAtPos(to);
        const box = {
          left: Math.min(a.left, b.left),
          right: Math.max(a.right, b.right),
          top: Math.min(a.top, b.top),
          bottom: Math.max(a.bottom, b.bottom),
        };

        const toolbarRect = toolbarEl.getBoundingClientRect();

        // Anchor at the horizontal center of the selection/caret.
        const anchorX = (box.left + box.right) / 2;
        const anchorTop = box.top;
        const gap = 10;

        // We render the toolbar with `position: fixed` (viewport coordinates).
        // Clamp so it stays inside the viewport while still staying "above" the selection.
        const pad = 12;
        const viewportW = window.innerWidth || document.documentElement?.clientWidth || 0;

        let left = anchorX;
        const minLeft = pad + toolbarRect.width / 2;
        const maxLeft = Math.max(minLeft, viewportW - pad - toolbarRect.width / 2);
        left = Math.min(maxLeft, Math.max(minLeft, left));

        // Because we translate the toolbar up by its height + gap, we need a minimum `top`
        // so the final toolbar doesn't go above the viewport.
        const minTop = toolbarRect.height + gap + pad;
        const top = Math.max(anchorTop, minTop);

        this.toolbarState.visible = true;
        this.toolbarState.left = left;
        this.toolbarState.top = top;
        this.toolbarState.placement = "top";
      } catch (e) {
        // If coordsAtPos fails (rare), just hide.
        this.toolbarState.visible = false;
      }
    },
    parseLinkAttrs() {
      if (!this.editor) return { href: "", target: "" };
      const attrs = this.editor.getAttributes("link") || {};
      return {
        href: String(attrs.href || ""),
        target: String(attrs.target || ""),
      };
    },
    parseHrefToForm(href, target) {
      const h = String(href || "").trim();
      const form = {
        type: "web",
        webUrl: h,
        emailAddress: "",
        emailSubject: "",
        emailBody: "",
        whatsappPhone: "",
        whatsappMessage: "",
        phoneNumber: "",
      };

      if (!h) return form;

      // Descadastro token
      if (h === "[[cliente.url_descadastro]]" || h.includes("[[cliente.url_descadastro]]")) {
        form.type = "descadastro";
        form.webUrl = "";
        return form;
      }

      // mailto
      if (/^mailto:/i.test(h)) {
        const rest = h.replace(/^mailto:/i, "");
        const [addrPart, queryPart] = rest.split("?");
        form.type = "email";
        form.emailAddress = decodeURIComponent(addrPart || "").trim();
        try {
          const params = new URLSearchParams(queryPart || "");
          form.emailSubject = params.get("subject") ? decodeURIComponent(params.get("subject")) : "";
          form.emailBody = params.get("body") ? decodeURIComponent(params.get("body")) : "";
        } catch (e) {
          // ignore
        }
        return form;
      }

      // tel
      if (/^tel:/i.test(h)) {
        form.type = "phone";
        form.phoneNumber = h.replace(/^tel:/i, "").trim();
        return form;
      }

      // wa.me
      const waMe = h.match(/^https?:\/\/wa\.me\/([^?/#]+)(?:\?(.+))?$/i);
      if (waMe) {
        form.type = "whatsapp";
        form.whatsappPhone = decodeURIComponent(waMe[1] || "");
        try {
          const params = new URLSearchParams(waMe[2] || "");
          form.whatsappMessage = params.get("text") ? decodeURIComponent(params.get("text")) : "";
        } catch (e) {
          // ignore
        }
        return form;
      }

      // api.whatsapp.com/send?phone=&text=
      if (/^https?:\/\/api\.whatsapp\.com\/send/i.test(h)) {
        form.type = "whatsapp";
        try {
          const u = new URL(h);
          form.whatsappPhone = u.searchParams.get("phone") || "";
          form.whatsappMessage = u.searchParams.get("text") || "";
        } catch (e) {
          // ignore
        }
        return form;
      }

      return form;
    },
    focusLinkPrimaryField() {
      const t = this.linkForm.type;
      const map = {
        web: "linkWebInput",
        email: "linkEmailInput",
        whatsapp: "linkWhatsappPhoneInput",
        phone: "linkPhoneInput",
      };
      const ref = map[t] || "";
      if (!ref) return;
      const el = this.$refs[ref];
      el?.focus?.();
      el?.select?.();
    },
    safeLinkHref(raw) {
      const v = String(raw || "").trim();
      if (!v) return "";
      if (v.startsWith("#")) return v;
      if (/^(https?:|mailto:|tel:)/i.test(v)) return v;
      // allow bare domains (common user input)
      if (/^[a-z0-9.-]+\.[a-z]{2,}([/?#].*)?$/i.test(v)) return `https://${v}`;
      return v;
    },
    safePhone(raw) {
      const v = String(raw || "").trim();
      if (!v) return "";
      const plus = v.startsWith("+");
      const digits = v.replace(/\D/g, "");
      if (!digits) return "";
      return plus ? `+${digits}` : digits;
    },
    safeWhatsappPhone(raw) {
      const v = String(raw || "").trim();
      if (!v) return "";
      // wa.me expects digits only (country code + number)
      const digits = v.replace(/\D/g, "");
      if (!digits) return "";
      // If user typed BR number in local format (DDD + number), prefix 55.
      if ((digits.length === 10 || digits.length === 11) && !digits.startsWith("55")) return `55${digits}`;
      return digits;
    },
    ensureValidWebUrlOrSetError() {
      if (this.linkForm.type !== "web") return true;
      if (this.isWebUrlValid) return true;
      this.linkErrors.webUrl = this.$_("visualEditor-emailEditor-tiptap-link-web-invalid");
      return false;
    },
    buildLinkAttrsFromForm() {
      const type = this.linkForm.type;
      const withBlank = (attrs) => {
        if (!attrs?.href) return attrs;
        return {
          ...attrs,
          target: "_blank",
          rel: "noopener noreferrer",
        };
      };
      if (type === "descadastro") {
        return withBlank({ href: "[[cliente.url_descadastro]]" });
      }
      if (type === "web") {
        const href = String(this.linkForm.webUrl || "").trim();
        return withBlank({ href });
      }
      if (type === "email") {
        const to = String(this.linkForm.emailAddress || "").trim();
        if (!to) return { href: "" };
        const subject = String(this.linkForm.emailSubject || "");
        const body = String(this.linkForm.emailBody || "");
        const qp = [];
        if (subject) qp.push(`subject=${encodeURIComponent(subject)}`);
        if (body) qp.push(`body=${encodeURIComponent(body)}`);
        const qs = qp.length ? `?${qp.join("&")}` : "";
        return withBlank({ href: `mailto:${to}${qs}` });
      }
      if (type === "whatsapp") {
        const phone = this.safeWhatsappPhone(this.linkForm.whatsappPhone);
        if (!phone) return { href: "" };
        const msg = String(this.linkForm.whatsappMessage || "");
        const qs = msg ? `?text=${encodeURIComponent(msg)}` : "";
        return withBlank({ href: `https://wa.me/${phone}${qs}` });
      }
      // phone
      const phone = this.safePhone(this.linkForm.phoneNumber);
      if (!phone) return { href: "" };
      return withBlank({ href: `tel:${phone}` });
    },
    toggleBold() {
      this.editor?.chain().focus().toggleBold().run();
    },
    toggleItalic() {
      this.editor?.chain().focus().toggleItalic().run();
    },
    toggleUnderline() {
      this.editor?.chain().focus().toggleUnderline().run();
    },
    toggleStrike() {
      this.editor?.chain().focus().toggleStrike().run();
    },
    applyTitle() {
      if (!this.editor) return;
      this.editor
        .chain()
        .focus()
        .setFontSize("32px")
        .setLineHeight("1.2")
        .setFontWeight("700")
        .run();
    },
    applySubtitle() {
      if (!this.editor) return;
      this.editor
        .chain()
        .focus()
        .setFontSize("24px")
        .setLineHeight("1.2")
        .setFontWeight("700")
        .run();
    },
    setColor(color) {
      if (!this.editor) return;
      this.editor.chain().focus().setColor(color).run();
    },
    setBackgroundColor(color) {
      if (!this.editor) return;
      this.editor.chain().focus().setBackgroundColor(color).run();
    },
    unsetBackgroundColor() {
      if (!this.editor) return;
      this.editor.chain().focus().unsetBackgroundColor().run();
    },
    setFontFamily(fontFamily) {
      if (!this.editor) return;
      const v = String(fontFamily || "").trim();
      if (!v) {
        this.editor.chain().focus().unsetFontFamily().run();
      } else {
        this.editor.chain().focus().setFontFamily(v).run();
      }
    },
    setFontSize(size) {
      if (!this.editor) return;
      const n = Number(size);
      if (!Number.isFinite(n) || n <= 0) {
        this.editor.chain().focus().unsetFontSize().run();
        return;
      }
      this.editor.chain().focus().setFontSize(`${n}px`).run();
    },
    setLineHeight(raw) {
      if (!this.editor) return;
      const v = String(raw || "").trim();
      if (!v) {
        this.editor.chain().focus().unsetLineHeight().run();
        return;
      }
      this.editor.chain().focus().setLineHeight(v).run();
    },
    toggleLinkUi() {
      if (!this.editor) return;
      if (this.linkUiOpen) {
        this.closeLinkUi();
        return;
      }
      const { href, target } = this.parseLinkAttrs();
      this.linkForm = this.parseHrefToForm(href, target);
      this.linkUiOpen = true;
      this.$nextTick(() => {
        this.focusLinkPrimaryField();
      });
    },
    closeLinkUi() {
      this.linkUiOpen = false;
      // don't clear form; keeps user input if bubble menu flickers
    },
    applyLinkForm() {
      if (!this.editor) return;
      if (!this.ensureValidWebUrlOrSetError()) return;
      if (!this.isLinkFormValid) return;
      const attrs = this.buildLinkAttrsFromForm();
      if (!attrs?.href) {
        this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
        this.closeLinkUi();
        return;
      }
      this.editor.chain().focus().extendMarkRange("link").setLink(attrs).run();
      this.closeLinkUi();
    },
    removeLink() {
      if (!this.editor) return;
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
      this.closeLinkUi();
    },
  },
};
</script>

<style scoped lang="less">
.kv-ve-text {
  width: 100%;
  max-width: 100%;
  position: relative;
}

.kv-ve-text__content {
  width: 100%;
}

.kv-ve-text__snippets {
  position: fixed;
  z-index: 21000;
  width: auto;
  max-height: 240px;
  overflow: auto;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid #e5e7eb;
  box-shadow: 0 20px 50px rgba(16, 24, 40, 0.22);
  padding: 6px;
}
.kv-ve-text__snippet-item {
  all: unset;
  cursor: pointer;
  border-radius: 4px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  border: 1px solid transparent;
  text-align: left;
}
.kv-ve-text__snippet-item:hover {
  background: rgba(42, 170, 76, 0.08);
}
.kv-ve-text__snippet-item.active {
  background: rgba(42, 170, 76, 0.10);
  border-color: rgba(42, 170, 76, 0.35);
}
.kv-ve-text__snippet-token {
  font-size: 12px;
  font-weight: 500;
  color: #101828;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

::v-deep .ProseMirror {
  outline: none;
  min-height: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  /* Kevi has `* { font-family: Montserrat }` applied directly to every element.
     Force our base font on ProseMirror, but do NOT override spans that have inline styles from TipTap. */
  font-family: var(--kv-ve-font-family, Helvetica, Arial, sans-serif);
  font-weight: 400;
  color: inherit;
  /* Match preview whitespace behavior (email HTML collapses spaces). */
  white-space-collapse: collapse;
  white-space: normal;
}

/* Ensure every descendant inherits the ProseMirror font-family, unless TipTap explicitly sets one inline.
   This beats Kevi's global `* { font-family: Montserrat }` without breaking toolbar font changes. */
::v-deep .ProseMirror *:not([style*="font-family"]) {
  font-family: inherit !important;
}

/* Same for font-weight: keep base weight consistent, but allow inline weights (span style="font-weight:...") to win. */
::v-deep .ProseMirror *:not([style*="font-weight"]) {
  font-weight: inherit !important;
}

/* Apply default link color from email design settings, but allow inline color styles to override. */
::v-deep .ProseMirror a:not([style*="color"]) {
  color: var(--kv-ve-link-color, #0ea5e9) !important;
}

/* Ensure line-height from TipTap textStyle marks is respected.
   Line-height on inline elements doesn't work as expected, so we make spans with line-height inline-block. */
::v-deep .ProseMirror span[style*="line-height"] {
  display: inline-block;
}

/* Ensure Bold is always visible (TipTap uses <strong>/<b> by default). */
::v-deep .ProseMirror strong,
::v-deep .ProseMirror b {
  font-weight: 700 !important;
}

::v-deep .ProseMirror > * {
  margin: 0;
}

/* Bubble toolbar (floats over selection) */
.kv-ve-text__toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 6px -2px rgba(16, 24, 40, 0.06), 0 10px 15px -3px rgba(16, 24, 40, 0.08);
  flex-wrap: nowrap;
  width: max-content;
  max-width: calc(100vw - 24px);
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}

.kv-ve-text__toolbar--bubble {
  position: fixed;
  z-index: 2600;
  pointer-events: auto;
}

/* Mobile: disable native selection for non-selected text blocks, but allow it while editing */
@media (max-width: 768px) {
  .kv-ve-text:not(.kv-ve-text--selected) {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }
  .kv-ve-text:not(.kv-ve-text--selected) ::v-deep .ProseMirror {
    -webkit-user-select: none;
    user-select: none;
  }
  .kv-ve-text.kv-ve-text--selected ::v-deep .ProseMirror {
    -webkit-touch-callout: none; /* prevent iOS callout menu while still allowing selection/caret */
    -webkit-user-select: text;
    user-select: text;
  }
}

.kv-ve-text__btn {
  all: unset;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #344054;
  border: 1px solid transparent;
  background: transparent;
}

.kv-ve-text__btn:hover {
  color: #2aaa4c;
}

.kv-ve-text__btn:focus-visible {
  border-color: rgba(42, 170, 76, 0.85);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.14);
}

.kv-ve-text__btn.active {
  color: #2aaa4c;
}

.kv-ve-text__btn .mdi {
  font-size: 20px;
  line-height: 1;
}

.kv-ve-text__select {
  height: 34px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  padding: 0 8px;
  font-size: 13px;
  color: #344054;
  outline: none;
}
.kv-ve-text__select:hover {
  border-color: #d0d5dd;
}
.kv-ve-text__select:focus {
  border-color: rgba(42, 170, 76, 0.65);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.14);
}
.kv-ve-text__select--size {
  width: auto;
}

.kv-ve-text__color-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  margin: 0;
  line-height: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  padding: 0;
  user-select: none;
}
.kv-ve-text__color-btn:hover {
  border-color: rgba(42, 170, 76, 0.65);
}
.kv-ve-text__color-btn:focus-within {
  border-color: rgba(42, 170, 76, 0.65);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.14);
}

.kv-ve-text__icon-a {
  font-weight: 800;
  font-size: 16px;
  line-height: 1;
  color: var(--kv-ve-color, #111827);
}

.kv-ve-text__icon-bg {
  width: 29px;
  height: 29px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: var(--kv-ve-bg, transparent);
  border: 1px solid rgba(16, 24, 40, 0.10);
}
/* If background is transparent, show a subtle checkerboard */
.kv-ve-text__color-btn--bg .kv-ve-text__icon-bg {
  background-image:
    linear-gradient(45deg, rgba(16, 24, 40, 0.08) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(16, 24, 40, 0.08) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, rgba(16, 24, 40, 0.08) 75%),
    linear-gradient(-45deg, transparent 75%, rgba(16, 24, 40, 0.08) 75%);
  background-size: 10px 10px;
  background-position: 0 0, 0 5px, 5px -5px, -5px 0px;
  background-color: #fff;
}
.kv-ve-text__color-btn--bg .kv-ve-text__icon-bg {
  /* paint actual bg color above checkerboard when not transparent */
  background-blend-mode: normal;
}
.kv-ve-text__icon-a--on-bg {
  font-weight: 800;
}

.kv-ve-text__color-btn input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  border: 0;
  padding: 0;
  margin: 0;
  appearance: none;
  -webkit-appearance: none;
}

.kv-ve-text__bg-wrap {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.kv-ve-text__bg-clear {
  all: unset;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #667085;
  border: 1px solid #e5e7eb;
  background: #fff;
}
.kv-ve-text__bg-clear:hover {
  color: #344054;
  border-color: #d0d5dd;
}
.kv-ve-text__bg-clear .mdi {
  font-size: 14px;
  line-height: 1;
}

.kv-ve-text__btn--strike .mdi {
  /* This glyph reads visually heavier than the others; nudge size down slightly. */
  font-size: 18px;
}

.kv-ve-text__link-wrap {
  display: inline-flex;
  align-items: center;
}

.kv-ve-text__modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(16, 24, 40, 0.45);
  /* Must be above TipTap BubbleMenu (tippy) which commonly uses z-index ~9999 */
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}

.kv-ve-text__modal {
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(16, 24, 40, 0.22);
  overflow: hidden;
}

.kv-ve-text__link-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f9fafb;
  border-bottom: 1px solid #eef2f6;
}
.kv-ve-text__link-title {
  font-weight: 700;
  font-size: 14px;
  color: #101828;
}
.kv-ve-text__link-close {
  all: unset;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #667085;
}
.kv-ve-text__link-close:hover {
  color: #2aaa4c;
}

.kv-ve-text__link-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.kv-ve-text__link-label {
  font-size: 12px;
  font-weight: 600;
  color: #344054;
  margin: 0;
}
.kv-ve-text__link-error {
  font-size: 12px;
  color: #b42318;
  margin-top: -4px;
}
.kv-ve-text__link-descadastro-hint {
  font-size: 13px;
  color: #475467;
  padding: 10px 12px;
  border: 1px dashed #d0d5dd;
  border-radius: 10px;
  background: #fcfcfd;
}
.kv-ve-text__link-select,
.kv-ve-text__link-input,
.kv-ve-text__link-textarea {
  width: 100%;
  border-radius: 10px;
  border: 1px solid #d0d5dd;
  background: #fff;
  padding: 10px 12px;
  font-size: 13px;
  color: #101828;
  outline: none;
  box-shadow: none;
}
.kv-ve-text__link-select,
.kv-ve-text__link-input {
  height: 42px;
}
.kv-ve-text__link-textarea {
  min-height: 84px;
  resize: vertical;
}
.kv-ve-text__link-select:focus,
.kv-ve-text__link-input:focus,
.kv-ve-text__link-textarea:focus {
  border-color: rgba(42, 170, 76, 0.65);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.14);
}

.kv-ve-text__checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  user-select: none;
  font-size: 13px;
  color: #344054;
}
.kv-ve-text__checkbox input {
  width: 18px;
  height: 18px;
}

.kv-ve-text__link-actions {
  display: flex;
  gap: 10px;
  margin-top: 6px;
}
.kv-ve-text__link-remove {
  all: unset;
  flex: 1;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #344054;
  font-weight: 600;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.kv-ve-text__link-remove:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.kv-ve-text__link-remove:hover:not(:disabled) {
  border-color: #d0d5dd;
}
.kv-ve-text__link-save {
  all: unset;
  flex: 1;
  height: 40px;
  border-radius: 10px;
  background: rgba(42, 170, 76, 0.20);
  color: #00542e;
  font-weight: 700;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.kv-ve-text__link-save:hover {
  background: rgba(42, 170, 76, 0.28);
}
.kv-ve-text__link-save:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.kv-ve-text__sep {
  width: 1px;
  height: 18px;
  background: #e5e7eb;
  margin: 0;
}
</style>


